import React, { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useGetBranding, BrandingColors } from "@/hooks/data/branding";
import { isInstagramBrowser } from "@/utils/browserEnv";

const MenuBrandingContext = createContext<BrandingColors | null>(null);

export const useMenuBranding = () => useContext(MenuBrandingContext);

interface MenuBrandingWrapperProps {
  children: React.ReactNode;
}

const MenuBrandingInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: brandingResponse } = useGetBranding({ enabled: true });
  const b = brandingResponse?.data;
  
  const isInstagram = useMemo(() => isInstagramBrowser(), []);

  // Build CSS custom properties style object only for non-null values
  const cssVars: Record<string, string> = {};

  if (b) {
    if (b.background) cssVars["--brand-bg"] = b.background;
    if (b.item_text) cssVars["--brand-item-text"] = b.item_text;
    if (b.item_border_no_image) cssVars["--brand-item-border"] = b.item_border_no_image;
    if (b.item_card_background) cssVars["--brand-item-card-bg"] = b.item_card_background;
    if (b.name_price_divider) cssVars["--brand-divider"] = b.name_price_divider;
    if (b.price) cssVars["--brand-price"] = b.price;
    if (b.navbar_background) cssVars["--brand-navbar-bg"] = b.navbar_background;
    if (b.navbar_icon) cssVars["--brand-navbar-icon"] = b.navbar_icon;
    if (b.navbar_favicon) cssVars["--brand-navbar-favicon"] = b.navbar_favicon;
    if (b.navbar_favicon_text) cssVars["--brand-navbar-favicon-text"] = b.navbar_favicon_text;
    if (b.navbar_favicon_background) cssVars["--brand-navbar-favicon-bg"] = b.navbar_favicon_background;
    if (b.language_text) cssVars["--brand-lang-text"] = b.language_text;
    if (b.language_button) cssVars["--brand-lang-btn"] = b.language_button;
    if (b.language_button_border) cssVars["--brand-lang-btn-border"] = b.language_button_border;
    if (b.social_media_icon) cssVars["--brand-social-icon"] = b.social_media_icon;
    if (b.social_media_background) cssVars["--brand-social-bg"] = b.social_media_background;
    if (b.hover_button) cssVars["--brand-hover-btn"] = b.hover_button;
    if (b.hover_button_text) cssVars["--brand-hover-btn-text"] = b.hover_button_text;
    if (b.qr_code_color) cssVars["--brand-qr-color"] = b.qr_code_color;
    if (b.bottom_sheet_background) cssVars["--brand-bottom-sheet-bg"] = b.bottom_sheet_background;
    if (b.bottom_sheet_menu_text) cssVars["--brand-bottom-sheet-text"] = b.bottom_sheet_menu_text;
  }

  return (
    <MenuBrandingContext.Provider value={b || null}>
      <div 
        className={`menu-branding-scope ${!isInstagram ? "disable-pull-to-refresh" : ""}`} 
        style={cssVars as React.CSSProperties}
      >
        {children}
      </div>
    </MenuBrandingContext.Provider>
  );
};

export const MenuBrandingWrapper: React.FC<MenuBrandingWrapperProps> = ({ children }) => {
  const location = useLocation();
  const isMenuPath = location.pathname === "/menu";

  if (!isMenuPath) {
    return <>{children}</>;
  }

  return <MenuBrandingInner>{children}</MenuBrandingInner>;
};
